import { useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, Eye, Filter, MailCheck, MailOpen, RotateCcw } from "lucide-react";

import { API_GET_APPRAISAL_DOCUMENT } from "../../../config/Endpoints.js";
import DocumentViewer from "../../../components/DocumentViewer.jsx";
import { searchAppraisals, scheduleAppraisals } from "../../components/features/appraisal-workflow/appraisal-api.ts";
import styles from "../../components/features/appraisal-workflow/appraisal-workflow.module.css";
import type { AppraisalRow } from "../../components/features/appraisal-workflow/types.ts";
import { AppraisalEmployeeAvatar, formatDate, useAppraisalSearch } from "../../components/features/appraisal-workflow/workflow-shared.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Button, DatePicker, DatePickerFieldTrigger, DateRange, Modal, Select } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import { getApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";

const STATUS_OPTIONS = [
  { value: "PDF_GENERATED", label: "Pending Email Schedule" },
  { value: "EMAIL_SCHEDULED", label: "Email Queued" },
  { value: "EMAIL_SENT", label: "Email Delivered" },
];

const SUMMARY = [
  { status: "PDF_GENERATED", label: "Pending Email Schedule", icon: CheckCircle2, tone: "orange" },
  { status: "EMAIL_SCHEDULED", label: "Email Queued", icon: MailCheck, tone: "green" },
  { status: "EMAIL_SENT", label: "Email Delivered", icon: MailOpen, tone: "blue" },
] as const;

type ScheduleTableRow = AppraisalRow & Record<string, unknown>;
type DraftFilters = { employeeId: string; status: string; startDate: Date | null; endDate: Date | null };
type DocumentDetail = { url: string; type: string; name: string };
const EMPTY_FILTERS: DraftFilters = { employeeId: "", status: "PDF_GENERATED", startDate: null, endDate: null };
const EMPTY_DOCUMENT: DocumentDetail = { url: "", type: "", name: "" };

function localIso(date: Date | null): string {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function selectedDateWithCurrentTime(date: Date): string {
  const now = new Date();
  const time = [now.getHours(), now.getMinutes(), now.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
  return `${localIso(date)}T${time}`;
}

function letterTypeLabel(value: string): string {
  return value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function scheduledDateLabel(value?: string | null): string {
  return formatDate(value?.split("T")[0]);
}

export default function ScheduleAppraisalPage() {
  const search = useAppraisalSearch("PDF_GENERATED", true);
  const employees = useEmployeeNameOptions();
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(EMPTY_FILTERS);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string | number>>(new Set());
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
  const [documentDetail, setDocumentDetail] = useState<DocumentDetail>(EMPTY_DOCUMENT);

  const loadCounts = async () => {
    const entries = await Promise.all(SUMMARY.map(async ({ status }) => [
      status || "ALL",
      (await searchAppraisals({ employeeId: "", status, useHrStatus: true, letterType: "", fromDate: "", toDate: "", page: 0, size: 1 })).totalElements,
    ] as const));
    setCounts(Object.fromEntries(entries));
  };

  useEffect(() => { if (search.error) showError(search.error); }, [search.error]);
  useEffect(() => { void loadCounts().catch(() => undefined); }, []);
  useEffect(() => {
    if (!documentDetail.url) return;
    window.document.getElementById("open-schedule-appraisal-document")?.click();
  }, [documentDetail.url]);

  const selectableRows = search.rows.filter((row) => row.hrStatus === "PDF_GENERATED");
  const visibleSelectableIds = new Set(selectableRows.map((row) => row.id));
  const selectedVisibleIds = new Set(Array.from(selectedRowIds).filter((id) => visibleSelectableIds.has(Number(id))));

  const clearDocumentDetail = () => {
    setDocumentDetail((current) => {
      if (current.url) URL.revokeObjectURL(current.url);
      return EMPTY_DOCUMENT;
    });
  };

  const viewSignedPdf = async (row: ScheduleTableRow) => {
    if (!row.signedPdfPath) {
      showError("Signed appraisal PDF is not available.");
      return;
    }

    setViewLoadingId(row.id);

    try {
      const url = `${API_GET_APPRAISAL_DOCUMENT}?filepath=${encodeURIComponent(row.signedPdfPath)}`;
      const { data } = await getApi<Blob>(url, accessToken(), "blob");
      if (!data.size) throw new Error("The appraisal PDF is empty.");
      if (data.type.toLowerCase().includes("json")) {
        const response = JSON.parse(await data.text()) as { message?: string };
        throw new Error(response.message || "Unable to load the appraisal PDF.");
      }
      const contentType = row.signedPdfPath.toLowerCase().endsWith(".pdf") || data.type.toLowerCase().includes("pdf")
        ? "application/pdf"
        : data.type || "application/octet-stream";
      const fileUrl = URL.createObjectURL(new Blob([data], { type: contentType }));
      const fileName = row.signedPdfPath.split("/").filter(Boolean).pop()?.replace(/\.pdf$/i, "") || "Signed Appraisal Letter";

      setDocumentDetail((current) => {
        if (current.url) URL.revokeObjectURL(current.url);
        return { url: fileUrl, type: contentType, name: fileName };
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to open the signed appraisal PDF.");
    } finally {
      setViewLoadingId(null);
    }
  };

  const columns: ResponsiveColumn<ScheduleTableRow>[] = [
    {
      id: "employeeName", header: "Employee Name", accessor: "employeeName", sortable: true,
      render: (row) => <div className={styles.employeeCell}><AppraisalEmployeeAvatar employeeName={row.employeeName} photoPath={row.photoPath} /><div className={styles.employeeIdentity}><strong title={row.employeeName}>{row.employeeName}</strong><small>{row.employeeCode || row.designation || "-"}</small></div></div>,
      getTitleText: (row) => row.employeeName,
    },
    { id: "letterType", header: "Letter Type", accessor: "letterType", sortable: true, render: (row) => <span className={styles.letterBadge}>{letterTypeLabel(row.letterType)}</span>, nowrap: true },
    { id: "updatedAt", header: "Last Updated", sortable: true, getSortValue: (row) => row.updatedAt ?? row.createdAt ?? "", render: (row) => formatDate(row.updatedAt ?? row.createdAt), nowrap: true },
    { id: "scheduledAt", header: "Scheduled For", sortable: true, getSortValue: (row) => row.scheduledAt ?? "", render: (row) => scheduledDateLabel(row.scheduledAt), nowrap: true },
    {
      id: "action",
      header: "Action",
      render: (row) => (
        <Button
          size="sm"
          variant="muted-outline"
          startIcon={<Eye size={15} />}
          disabled={!row.signedPdfPath || viewLoadingId != null}
          onClick={() => void viewSignedPdf(row)}
        >
          {viewLoadingId === row.id ? "Opening..." : "View"}
        </Button>
      ),
      nowrap: true,
    },
  ];

  const applyFilters = () => {
    setSelectedRowIds(new Set());
    search.update({ employeeId: draftFilters.employeeId, status: draftFilters.status, useHrStatus: true, fromDate: localIso(draftFilters.startDate), toDate: localIso(draftFilters.endDate), page: 0 });
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setSelectedRowIds(new Set());
    search.update({ employeeId: "", status: "PDF_GENERATED", useHrStatus: true, fromDate: "", toDate: "", page: 0 });
  };

  const closeModal = () => {
    if (busy) return;
    setModalOpen(false);
    setScheduledDate(null);
  };

  const scheduleSelected = async () => {
    const appraisalIds = Array.from(selectedVisibleIds).map(Number).filter(Number.isFinite);
    if (!appraisalIds.length) { showError("Select at least one appraisal that is ready to schedule."); return; }
    if (!scheduledDate) { showError("Select the email delivery date."); return; }
    setBusy(true);
    try {
      const response = await scheduleAppraisals(appraisalIds, selectedDateWithCurrentTime(scheduledDate), true);
      showSuccess(response.message ?? `${appraisalIds.length} appraisal email${appraisalIds.length === 1 ? "" : "s"} scheduled successfully.`);
      setSelectedRowIds(new Set());
      setModalOpen(false);
      setScheduledDate(null);
      await Promise.all([search.load(), loadCounts()]);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to schedule the selected appraisal emails.");
    } finally { setBusy(false); }
  };

  return <PageShell><div className={`${styles.approvalPage} ${styles.approvalPageClosed}`}>
    <div className={styles.approvalSummaryGrid}>{SUMMARY.map(({ status, label, icon: Icon, tone }) => <div key={status || "all"} className={`${styles.summaryCard} ${styles.summaryCardStatic}`}><span className={`${styles.summaryIcon} ${styles[`tone${tone}`]}`}><Icon size={20} /></span><span><strong>{label}</strong><b>{counts[status || "ALL"] ?? "--"}</b></span></div>)}</div>
    <section className={styles.listPanel}>
      <Card className={styles.approvalFiltersCard}><div className={styles.scheduleFiltersGrid}>
        <Select label="Employee Name" placeholder={employees.loading ? "Loading Employees..." : "Select Employee Name"} searchable isClearable options={employees.options} value={draftFilters.employeeId} disabled={employees.loading || search.loading} onChange={(employeeId) => setDraftFilters((current) => ({ ...current, employeeId }))} />
               <DateRange label="Effective Date" placeholderText="Select Effective Date" startDate={draftFilters.startDate} endDate={draftFilters.endDate} disabled={search.loading} onChange={([startDate, endDate]) => setDraftFilters((current) => ({ ...current, startDate, endDate }))} />

        <Select label="Status" placeholder="Select Status" options={STATUS_OPTIONS} value={draftFilters.status} disabled={search.loading} onChange={(status) => setDraftFilters((current) => ({ ...current, status }))} />
        <div className={styles.approvalFilterActions}><Button variant="accent" startIcon={<Filter size={16} />} disabled={search.loading} onClick={applyFilters}>Apply Filter</Button><Button variant="light-grey" startIcon={<RotateCcw size={16} />} disabled={search.loading} onClick={clearFilters}>Clear Filter</Button></div>
      </div></Card>
      <ResponsiveDataTable<ScheduleTableRow>
        aria-label="Appraisal email scheduling" columns={columns} data={search.rows as ScheduleTableRow[]}
        selection={{ getRowId: (row) => row.id, isRowDisabled: (row) => row.hrStatus !== "PDF_GENERATED", selectedRowIds: selectedVisibleIds, onSelectionChange: (ids) => setSelectedRowIds(new Set(Array.from(ids).filter((id) => visibleSelectableIds.has(Number(id))))), ariaLabel: "Select all appraisals ready to schedule on this page" }}
        page={search.filters.page + 1} pageSize={search.filters.size} totalItems={search.totalElements}
        onPageChange={(page) => { setSelectedRowIds(new Set()); search.update({ page: page - 1 }); }} onPageSizeChange={(size) => { setSelectedRowIds(new Set()); search.update({ size, page: 0 }); }}
        emptyState={<div className={styles.approvalEmptyState}>{search.loading ? "Loading Records..." : "No appraisal emails found"}</div>}
        totalLabel={`Total Records: ${search.totalElements}`}
      />
      {selectedVisibleIds.size ? <div className={styles.bulkActionBar}><span>{selectedVisibleIds.size} ready appraisal{selectedVisibleIds.size === 1 ? "" : "s"} selected</span><Button startIcon={<CalendarClock size={16} />} onClick={() => setModalOpen(true)}>Schedule Email</Button></div> : null}
    </section>

    <Modal open={modalOpen} onClose={closeModal} title="Schedule Appraisal Email Delivery" size="md" bodyClassName={styles.scheduleModalBody} footer={<div className={styles.scheduleModalActions}><Button variant="muted-outline" disabled={busy} onClick={closeModal}>Cancel</Button><Button startIcon={<CalendarClock size={16} />} disabled={busy || !scheduledDate} onClick={() => void scheduleSelected()}>{busy ? "Scheduling..." : "Schedule Email"}</Button></div>}>
      <div className={styles.scheduleModalContent}>
        <div className={styles.scheduleModalIntro}><span><MailCheck size={22} /></span><div><strong>Choose when to send the appraisal emails</strong><p>The email delivery will be scheduled for {selectedVisibleIds.size} selected appraisal{selectedVisibleIds.size === 1 ? "" : "s"}.</p></div></div>
        <DatePicker id="bulk-appraisal-scheduled-date" label="Delivery Date" required minDate={new Date()} disabled={busy} selected={scheduledDate} placeholderText="Select delivery date" customInput={<DatePickerFieldTrigger />} onChange={setScheduledDate} />
      </div>
    </Modal>
    <button id="open-schedule-appraisal-document" type="button" data-bs-toggle="modal" data-bs-target="#staticBackdropDocumentViewer" style={{ display: "none" }} tabIndex={-1} aria-hidden />
    {documentDetail.url ? <DocumentViewer url={documentDetail.url} contentType={documentDetail.type} fileName={documentDetail.name} onClose={clearDocumentDetail} /> : null}
  </div></PageShell>;
}
